/**
 * Uploading a 教師參考手冊 PDF outside FilePicker.
 *
 * FilePicker owns the two-phase blob flow for the composer's content pickers,
 * but it is a mounted component with a 20vh dropzone — the wrong shape for the
 * Manage Templates LIST, where an admin wants to attach a manual to one row
 * without opening a dialog. This is the same three steps as a headless call:
 *
 *   request_upload -> PUT the bytes to the returned SAS -> confirm_upload
 *
 * `epack_id: 0` IS THE POINT and must not be "fixed" to the template's epack:
 * it is what keeps the manual out of ex_flutterv1's download manifest, out of
 * every school's storage quota, and out of a duplicated locale. See
 * DB/20260910_extra_locale_manual.sql.
 */
import axios from 'axios';

export const MANUAL_MAX_BYTES = 20 * 1024 * 1024;

/** Client-side gate. Returns an error STRING, or "" when the file is fine. */
export function manualFileError(file, strings = {}) {
  if (!file) {
    return strings.EXTRA_LOCALE_MANUAL_UPLOAD_FAILED || '上載失敗';
  }
  const name = String(file.name || '');
  if (!/\.pdf$/i.test(name)) {
    return strings.EXTRA_LOCALE_MANUAL_NOT_PDF || '教師參考手冊必須是 PDF 檔案。';
  }
  if (Number(file.size) > MANUAL_MAX_BYTES) {
    const mb = (MANUAL_MAX_BYTES / (1024 * 1024)).toFixed(0);
    return (strings.EXTRA_LOCALE_MANUAL_TOO_LARGE || '檔案過大（上限 {0}MB）。')
      .replace('{0}', mb);
  }
  return '';
}

/** Promisified API.request that throws on the legacy negative-result rule. */
function apiRequest(api, cmd, requestObj) {
  return new Promise((resolve, reject) => {
    api.request(
      cmd,
      requestObj,
      (res) => {
        const body = res && res.body;
        if (!body || typeof body !== 'object') {
          reject(new Error('Empty API response.'));
          return;
        }
        if (typeof body.result === 'number' && body.result < 0) {
          reject(new Error(body.message || cmd + ' failed.'));
          return;
        }
        resolve(body.result);
      },
      (err) => {
        const remarks = (err && err.body && err.body.remarks) ? err.body.remarks : '';
        reject(new Error(remarks || (cmd + ' failed.')));
      },
    );
  });
}

/**
 * Upload one PDF and return the confirmed `file` row
 * ({file_id, path, display_name}). Throws with a human-readable message.
 *
 * `onProgress(fraction)` is optional and reports 0..1 of the blob PUT, which is
 * the only step with any duration (these files run ~5MB).
 */
export async function uploadManualPdf(api, file, onProgress = null) {
  if (!api) {
    throw new Error('API not available.');
  }

  const requested = await apiRequest(api, 'request_upload', {
    epack_id: 0,
    files: [{ filename: file.name, extension: 'pdf', file_size: file.size }],
  });
  const registration = requested && Array.isArray(requested.files)
    ? requested.files[0] : null;
  if (!registration || registration.error) {
    throw new Error((registration && registration.error) || 'request_upload failed.');
  }

  await axios.put(registration.sas_url, file, {
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/pdf' },
    onUploadProgress: (e) => {
      if (onProgress && e && e.total) {
        onProgress(Math.min(1, (e.loaded || 0) / e.total));
      }
    },
  });

  const confirmed = await apiRequest(api, 'confirm_upload', {
    file_ids: [registration.file_id],
  });
  const row = confirmed && Array.isArray(confirmed.files)
    ? confirmed.files[0] : confirmed;
  if (!row || row.error) {
    throw new Error((row && row.error) || 'confirm_upload failed.');
  }
  return {
    file_id: row.file_id,
    path: row.path || registration.path || '',
    display_name: row.display_name || '',
  };
}

export default uploadManualPdf;
